import { observer } from 'mobx-react-lite';
import React from 'react';

import { Icon, IconMaintenance } from '@flixbus/honeycomb-icons-react';
import { Tag } from '@flixbus/honeycomb-react';

import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';
import { formatTime } from 'utils/datetime';

import { STATION_TEXT } from './text';

import styles from './styles.module.css';

const isMissing = (value) => value === null;

const Station = observer(({ station }) => {
	const { arrival, departure, code, name, timezone, isVirtual, isOperational } = station;
	const { selectedTimezone } = rideDetailsStore;
	const timezoneToShow = selectedTimezone === null ? timezone : selectedTimezone.value;
	const arrivalTime = isMissing(arrival) ? null : formatTime(arrival, { timezone: timezoneToShow });
	const departureTime = isMissing(departure)
		? null
		: formatTime(departure, { timezone: timezoneToShow });

	// Virtual (breakdown) stations always render as the intermediate
	// layout — centred badge + whichever of arrival/departure the surrounding
	// sequences provided. Treating them as edges (start/end) when only one
	// timestamp is present would push the badge to a corner and break the row.
	const isStart = !isVirtual && isMissing(arrivalTime);
	const isEnd = !isVirtual && isMissing(departureTime);
	const isInter = isVirtual || (!isStart && !isEnd);

	const renderIcon = () => {
		if (isStart)
			return <img alt="Start icon" className={styles.icon} src={require('./icons/start.png')} />;
		if (isEnd)
			return (
				<img
					alt="End icon "
					className={styles.icon}
					src={require('./icons/icon-end-station.png')}
				/>
			);
		if (isInter)
			return (
				<img
					alt="Next station icon"
					className={styles.icon}
					src={require('./icons/icon-inter-station.png')}
				/>
			);
	};

	return (
		<div
			className={`
                ${styles.station}
                ${isInter ? styles.inter : styles.edge} 
                ${isStart ? styles.left : ''} 
                ${isEnd ? styles.right : ''} 
                `}
		>
			{isVirtual ? (
				<div
					className={styles.breakdownBadge}
					data-testid="virtual-station-badge"
					aria-label={STATION_TEXT.breakdownBadgeAriaLabel}
				>
					<Icon InlineIcon={IconMaintenance} size={5} extraClasses={styles.breakdownIcon} />
				</div>
			) : (
				<>
					<div className={styles.header}>{code}</div>
					{isOperational ? (
						<Tag extraClasses={styles.operationalStopTag} small data-testid="operational-stop-tag">
							{STATION_TEXT.operationalStopLabel}
						</Tag>
					) : (
						<div className={styles.stname}>{name}</div>
					)}
				</>
			)}

			<div className={`${styles.timing}`}>
				{!isStart && (arrival || isVirtual) && (
					<>
						<span className={styles.time}>
							{arrival
								? formatTime(arrival, { timezone: timezoneToShow })
								: STATION_TEXT.missingTimestampPlaceholder}
						</span>
						{renderIcon()}
					</>
				)}
				{!isEnd && (departure || isVirtual) && (
					<>
						{renderIcon()}
						<span className={styles.time}>
							{departure
								? formatTime(departure, { timezone: timezoneToShow })
								: STATION_TEXT.missingTimestampPlaceholder}
						</span>
					</>
				)}
			</div>

			{isInter && <div className={styles.routeline} />}
		</div>
	);
});

export default Station;
